/* eslint-disable no-duplicate-case */
import React, { useEffect } from 'react';
import { useHistory, Switch, Route, Redirect, BrowserRouter as Router } from "react-router-dom";
import Enrollment from "Pages/Enrollment/Enrollment";
import EnrollmentFromServer from "Pages/Enrollment/EnrollmentFromServer";
import OnlineEnrollment from "Pages/Enrollment/OnlineEnrollment";
import Dashboard from "Pages/Dashboard/Dashboard";
import Response from "Pages/Response/Response";
import FundsPage from "Pages/FundsPage/FundsPage";
import MyAccounts from "Pages/MyAccounts/MyAccounts";
import ManageLimit from "Pages/ManageLimit/ManageLimit";
import Loading from "Components/Loading";
import ValidUserRoute from "Utils/Routes/ValidUserRoute";
import NotEnrolledUserRoute from "Utils/Routes/NotEnrolledUserRoute";
import TransactionRoute from "Utils/Routes/TransactionRoute";
import LimitEnabledRoute from "Utils/Routes/LimitEnabledRoute";
import Enrolled from "Utils/Routes/Enrolled";
import useSessionIdWatcher from 'Utils/Hooks/useSessionIdWatcher';
import AddAccount from './MyAccounts/AddAccount';
import DeleteAccount from './MyAccounts/DeleteAccount';

import './App.scss';
import './Theme.scss';
import useMode, { MODE } from "Utils/Hooks/useMode";
import useView, { VIEW } from "Utils/Hooks/useView";
import useTransactionAmount from "Utils/Hooks/useTransactionAmount";
import useFundType from "Utils/Hooks/useFundType";
import useRedirectUrl from 'Utils/Hooks/useRedirectUrl';
import useCancelRedirect from "Utils/Hooks/useCancelRedirect";
import { APP_ROOT } from 'Utils/EnvVars';
import { useFetch } from 'Utils/Hooks/useFetch';
import { IPADDRESSURL, PRODUCTTYPES, GPURLPATH, PAVILIONURLPATH, FUND_SOURCE_TYPE, Routes } from 'Utils/Constants';
import useNative from "Utils/Hooks/useNative";
import useOAuthStateId from 'Utils/Hooks/useOAuthStateId';
import useAppKey from 'Utils/Hooks/useAppKey';
import useSessionStorage from 'Utils/Hooks/useSessionStorage';

function App() {
  const mode = useMode(); // Cache mode selection
  const view = useView(); // Cache view selection
  useFundType(); // Cache fund type
  useFetch(IPADDRESSURL);
  useNative();
  useRedirectUrl();
  useCancelRedirect();
  useOAuthStateId();
  useAppKey();
  const watcher = useSessionIdWatcher();
  const session = watcher.session;
  const sessionError = watcher.error;
  const isOAuthRedirect = window.location.href.includes('oauth_state_id=');
  const [sessionStorageTransAmount, setSessionStorageTransAmount] = useSessionStorage('vip-transaction-amount');

  useTransactionAmount(session?.transactionAmount); // Cache transaction amount
  let history = useHistory();

  useEffect(() => {
    const tabCloseEvent = event => {
      const isRedirectOrCancel = window.sessionStorage.getItem("IsRedirectOrCancel") ? window.sessionStorage.getItem("IsRedirectOrCancel") : false;
      const isPlaidLaunch = window.sessionStorage.getItem("plaidlaunch") ? window.sessionStorage.getItem("plaidlaunch") : false;
      if (!isRedirectOrCancel && !isPlaidLaunch) {
        const body = {
          transactionStage: mode === MODE.withdraw ? 'Withdrawal' : 'Deposit'
        };
        const headers = {
          'session-id': JSON.parse(global.sessionStorage.getItem('vip-patron-session')).sessionId,
          'accept': 'application/json',
          'content-type': body && 'application/json',
          'authorization': 'PatronSession',
          'ipAddress': window.sessionStorage.getItem("clientIpAddress")
        };
        window.fetch(APP_ROOT + "/api/usertracking/cancelflow", {
          'method': 'POST',
          'headers': headers,
          'body': JSON.stringify(body)
        })
          .then((response) => {
            if (response.ok) {
            } else {
              if (response.status === 500) {
                console.error(response.statusText);
                history.push({
                  pathname: "/error",
                });
              } else if (response.status === 401) {
                console.error(response.statusText);
                history.push({
                  pathname: "/expired",
                });
              } else {
                response.json().then((data) => {
                  console.error(data);
                  onError(data);
                });
              }
            }
          })
          .catch((error) => {
            console.error(error);
            history.push({
              pathname: "/error",
            });
          });
      }
    };

    function onError(response) {
      history.push({
        pathname: '/error',
        state: {
          isError: true,
          showButton: true,
          title: "Request Failed. Please Try Again",
          message: response.patronErrorMessage ?? "Your Cancel Request Failed. Please Try Again",
          allowRetry: false
        }
      });
    }

    window.addEventListener('beforeunload', tabCloseEvent);

    return () => {
      window.removeEventListener('beforeunload', tabCloseEvent);
    };
  }, []);

  if (watcher.isLoading) return (<Loading />);
  const isVipEnrolled = session?.isVipEnrolled;
  const withdrawFlow = mode === MODE.withdraw;
  const depositFlow = mode === MODE.deposit;

  const expiredMessage = (session?.operatorName) ? `This session has expired. To make a new ${(withdrawFlow) ? 'withdrawal' : 'deposit'}, please go back to ${session.operatorName}.` : "Your session has expired or could not be found. Please close this window.";
  const sessionInvalid = sessionError || !session || session?.isExpired || (session?.expires && Date.parse(session.expires) <= Date.now());
  const isError = session && session.errorModel && session.errorModel.patronErrorMessage;
  const errorMessage = (session && session.errorModel && session.errorModel.patronErrorMessage) ? session.errorModel.patronErrorMessage : '';
  const productType = session?.productType;
  document.title = session?.patronProductType === PRODUCTTYPES.PREFERRED ? 'VIPP' : 'VIPO';

  const isCashierPage = view === VIEW.cashier;
  const cashierDepositUrl = "/cashierDeposit";
  const cashierWithdrawUrl = "/cashierWithdraw";
  const appStyle = isCashierPage ? "cashierPageApp" : "app";
  const isCashierRedirect = !isOAuthRedirect && session?.uiStartingAction === FUND_SOURCE_TYPE.PLAID && (session?.uiStartingPath?.includes(Routes.ADD_ACCOUNT) || session?.uiStartingPath?.includes(Routes.MY_ACCOUNTS));
  const isOperatorPreferred = session?.operatorProducts.includes(
    PRODUCTTYPES.PREFERRED
  );
  const isOperatorOnline = session?.operatorProducts.includes(
    PRODUCTTYPES.ONLINE
  );
  const isOperatorBoth = isOperatorOnline && isOperatorPreferred;

  return (
    <div className={appStyle}>
      {/* <Router basename={APP_ROOT.replace(/\/$/, "")}> */}
      <Router basename={window.location.pathname.replace(/\/$/, "") === APP_ROOT ? APP_ROOT.replace(/\/$/, "") : (window.location.pathname.replace(/\/$/, "") === GPURLPATH ? GPURLPATH : PAVILIONURLPATH)}>
        <Switch>
          {/* Index router */}
          <Route
            exact
            path="/"
            render={() => {
              if (isError) {
                return <Redirect to="/error" />;
              } else if (isOAuthRedirect && sessionInvalid) {
                return <Redirect to="/goback" />;
              } else if (sessionInvalid) {
                return <Redirect to="/expired" />;
              }
              else if (
                !isVipEnrolled &&
                (productType === PRODUCTTYPES.ONLINE || session?.toggles.productRoutingEnabled) && !isCashierRedirect && (isOperatorBoth || isOperatorOnline)
              ) {
                return <Redirect to="/onlineEnroll" />;
              }
              else if (!isVipEnrolled && !isCashierRedirect) {
                return <Redirect to="/enroll" />;
              }
              else if (isCashierRedirect) {
                return <Redirect to={session.uiStartingPath} />;
              }
              else if (isOAuthRedirect) {
                return <Redirect to="/addAccount" />;
              } else if (isVipEnrolled && withdrawFlow) {
                if (isCashierPage){
                  return <Redirect to={cashierWithdrawUrl} />;
                }
                else{
                  return <Redirect to="/withdrawFunds" />;
                }
              } else if (isVipEnrolled && depositFlow) {
                if (isCashierPage){
                  return <Redirect to={cashierDepositUrl} />;
                }
                else{
                  return <Redirect to="/addFunds" />;
                }
              } else {
                return <Redirect to="/invalid" />;
              }
            }}
          />
          {/* Main application */}
          <NotEnrolledUserRoute
            path="/enroll"
            component={EnrollmentFromServer}
            appProps={{ plaidRedirect: isOAuthRedirect }}
          />
          <NotEnrolledUserRoute
            path="/retry"
            component={Enrollment}
          />
          <NotEnrolledUserRoute
            path="/onlineEnroll"
            component={OnlineEnrollment}
            appProps={{ plaidRedirect: isOAuthRedirect }}
          />
          <Enrolled
            path="/dashboard"
            component={Dashboard}
          />
          <TransactionRoute
            path="/addFunds"
            component={FundsPage}
          />
          <TransactionRoute
            path="/withdrawFunds"
            component={FundsPage}
            appProps={{ withdraw: true }}
          />
          <TransactionRoute
            path="/cashierDeposit"
            component={FundsPage}
          />
          <TransactionRoute
            path="/cashierWithdraw"
            component={FundsPage}
            appProps={{ withdraw: true }}
          />
          <ValidUserRoute
            path="/myAccounts"
            component={MyAccounts}
          />
          <ValidUserRoute
            path="/addAccount"
            component={AddAccount}
            appProps={{ plaidRedirect: isOAuthRedirect, isCashierRedirect: isCashierRedirect }}
          />
          <ValidUserRoute
            path="/deleteAccount"
            component={DeleteAccount}
          />
          <LimitEnabledRoute
            path="/manageLimit"
            component={ManageLimit}
          />
          {/* <ValidUserRoute
            path="/accountInfo"
            component={AccountInfo}
          /> */}
          {/* Response shortcuts */}
          <Route exact path="/expired">
            <Response isError={true} onClickClose={true}
              title="Session Expired"
              message={expiredMessage}
              customButtons={[{
                label: (session?.operatorName) ? `CONTINUE TO ${session.operatorName?.toUpperCase()}` : 'Close',
                returnToOperator: true,
                returnUrl: session?.returnUrl,
                transactionId: session?.transactionId,
                transactionType: withdrawFlow ? "withdraw" : "deposit"
              }]}
            />
          </Route>
          <Route exact path="/goback">
            <Response isBack={true} title="Plaid Linked" message="Press the Back button to complete bank setup." />
          </Route>
          <Route exact path="/submit">
            <Response isError={false} />
          </Route>
          <Route exact path="/success">
            <Response isError={false} title="Success" message="You've successfully completed this action." />
          </Route>
          <Route exact path="/error">
            <Response isError={true} onClickClose={true} message={errorMessage} />
          </Route>
          <Route exact path="/invalid">
            <Response
              onClickClose={true}
              title="Invalid Entry"
              showHeader={false}
              message={(session?.operatorName) ? `Please access VIP Preferred from ${session.operatorName?.toUpperCase()}.` : 'Please close this window.'}
              customButtons={[{
                label: (session?.operatorName) ? `CONTINUE TO ${session.operatorName?.toUpperCase()}` : 'Close',
                returnToOperator: true,
                returnUrl: session?.returnUrl,
                transactionId: session?.transactionId,
                transactionType: withdrawFlow ? "withdraw" : "deposit"
              }]} />
          </Route>
          <Route exact path="/logout">
            <Response isError={false} title="Logged Out!" loggedOut={true}
              onClickClose={true}
              showHeader={false}
              message="You've been successfully logged out."
              customButtons={[{
                label: (session?.operatorName) ? `CONTINUE TO ${session.operatorName?.toUpperCase()}` : 'Close',
                returnToOperator: true,
                returnUrl: session?.returnUrl,
                transactionId: session?.transactionId,
                transactionType: withdrawFlow ? "withdraw" : "deposit"
              }]} />
          </Route>
          <Route exact path="/inprogress">
            <Response isError={false} showHeader={false} message="Mitek Verification is in progress"></Response>
          </Route>
          <Route render={() => <Redirect to='/dashboard' />} />
        </Switch>
      </Router>
    </div>
  );
}

export default App;